import { utilities } from '@weatherbug/common';

import { CurrentTrackPoint } from '../common/interfaces/types';
import { CurrentHurricaneDetailIconType, HurricaneWidgetSpotlightIconType } from './types';

import { baseHurricaneWidgetSpotlightIconExtension, 
    baseHurricaneWidgetSpotlightIconPath, 
    baseSelectedPastHurricaneDetailIconSubPath,
    baseSelectedHurricaneDetailIconPath,
    HurricaneTropicalDepressionType, 
    HurricaneTropicalStormType } from './constants';
import { defaultTextValue } from '../../common/utilities/constants';

const commonJSHurricaneUtil = new utilities.HurricaneUtil();

type BasePathType = typeof baseHurricaneWidgetSpotlightIconPath | typeof baseSelectedHurricaneDetailIconPath;

export const getHurricaneIconPath = (
    currentTrackPoint: CurrentTrackPoint, 
    basePath: BasePathType, 
    shouldGetPastIcons?: boolean): HurricaneWidgetSpotlightIconType | CurrentHurricaneDetailIconType | undefined => {

    if (!basePath && !currentTrackPoint) {
        return;
    }

    const currentDType = currentTrackPoint?.dtype;
    const currentT = currentTrackPoint.t;
    
    if (currentDType === commonJSHurricaneUtil.pastDtype && shouldGetPastIcons && basePath) {
        const basePasttIconPath = `${basePath}${baseSelectedPastHurricaneDetailIconSubPath}`;
        return getCurrentHurricaneIconPaths(currentT, currentTrackPoint, basePasttIconPath as BasePathType) as CurrentHurricaneDetailIconType;
    }

    return getCurrentHurricaneIconPaths(currentT, currentTrackPoint, basePath);
};

const getCurrentHurricaneIconPaths = (currentT: number, 
    currentTrackPoint: CurrentTrackPoint, 
    basePath: typeof baseHurricaneWidgetSpotlightIconPath | typeof baseSelectedHurricaneDetailIconPath): HurricaneWidgetSpotlightIconType | CurrentHurricaneDetailIconType | undefined => {

    if (currentT === commonJSHurricaneUtil.hurricaneType && currentTrackPoint?.categoryNumber) {
        /**
         * Returns one of the following icon paths:
         * 
         * | "[basePath]_1.png" 
         * | "[basePath]_2.png" 
         * | "[basePath]_3.png" 
         * | "[basePath]_4.png" 
         * | "[basePath]_5.png"
         * | "[basePath]_past_1.png" 
         * | "[basePath]_past_2.png" 
         * | "[basePath]_past_3.png" 
         * | "[basePath]_past_4.png" 
         * | "[basePath]_past_5.png"
         */
        return `${basePath}${currentTrackPoint.categoryNumber}${baseHurricaneWidgetSpotlightIconExtension}`;

    } else if (commonJSHurricaneUtil.tropicalStormTypes.includes(currentT)) {
        /**
         * Returns path to Tropical Storm Icon:
         * 
         * "[basePath]_TS.png"
         * "[basePath]_past_TS.png"
         */
        return `${basePath}${HurricaneTropicalStormType}${baseHurricaneWidgetSpotlightIconExtension}`;

    } else if (commonJSHurricaneUtil.tropicalDepressionTypes.includes(currentT)) {
        /**
         * Returns Path to Tropical Depression Icon:
         * 
         *  "[basePath]_TD.png"
         *  "[basePath]_past_TD.png"
         */
        return `${basePath}${HurricaneTropicalDepressionType}${baseHurricaneWidgetSpotlightIconExtension}`;
    }

    return;
};

export const validateHurricaneDetailData = (detailItem: string | number | null | undefined): string | number => {
    if (!detailItem) {
        return defaultTextValue;
    }

    return detailItem;
};

export const hurricaneAdSlotIds = {
    mobile: {
        hurricaneTopBanner: 'wb_top_banner_hurricane_mobile_320x50',
        hurricaneInfoSection: 'hurricane_info_section_mobile_300x250',
        hurricaneStorySection: 'hurricane_story_section_mobile_300x250'
    },
    tablet: {
        hurricaneTopBanner: 'wb_top_banner_hurricane_tablet_728x90',
        hurricaneInfoSection: 'hurricane_info_section_tablet_300x250',
        hurricaneStorySection: 'hurricane_story_section_tablet_300x600'
    },
    desktop: {
        hurricaneTopBanner: 'wb_banner', // wb_banner is the default used acrossed website - hence changing it will affect several pages or increase scope of task
        hurricaneInfoSection: 'hurricane_info_section_desktop_300x250',
        hurricaneStorySection: 'hurricane_story_section_desktop_300x600'
    }
}